import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Slider } from "primereact/slider";
import { SelectButton } from "primereact/selectbutton";
import {
  apiService,
  Person,
  Availability,
  ScheduleRemark,
  ScheduleRemarkType,
  AppRole,
  PersonRoleAssignment,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useSearch } from "../../contexts/SearchContext";
import { useApiAuth } from "../../hooks/useApiAuth";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";
import { MultiSelect } from "primereact/multiselect";
import { getCurrentWeek } from "../../utils/weekUtils";
import { WeekPicker } from "../../components/WeekPicker";
import {
  formatScheduleRemarkDays,
  formatScheduleRemarkRange,
  formatScheduleRemarkType,
  joinScheduleRemarkDays,
  parseScheduleRemarkDays,
  SCHEDULE_REMARK_WEEKDAYS,
  ScheduleRemarkWeekday,
  weekIdFromParts,
  weekPartsFromId,
} from "../../utils/scheduleRemarkUtils";

const remarkTypeOptions: { label: string; value: ScheduleRemarkType }[] = [
  { label: "Off", value: "off" },
  { label: "Constraint", value: "constraint" },
  { label: "General note", value: "note" },
];

const PeopleAdmin: React.FC = () => {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchTerm: filter, setSearchTerm: setFilter } = useSearch();
  const [modalVisible, setModalVisible] = useState(false);
  const [availabilityModalVisible, setAvailabilityModalVisible] =
    useState(false);
  const [editPerson, setEditPerson] = useState<Person | null>(null);
  const [selectedPersonForAvailability, setSelectedPersonForAvailability] =
    useState<Person | null>(null);

  // Form fields for person
  const [formName, setFormName] = useState("");
  const [formSurname, setFormSurname] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPlannable, setFormPlannable] = useState(true);

  // Form fields for availability
  const [availabilityStartWeek, setAvailabilityStartWeek] = useState(
    getCurrentWeek()
  );
  const [availabilityStartYear, setAvailabilityStartYear] = useState(
    new Date().getFullYear()
  );
  const [availabilityHours, setAvailabilityHours] = useState(40);

  const [remarksModalVisible, setRemarksModalVisible] = useState(false);
  const [selectedPersonForRemarks, setSelectedPersonForRemarks] =
    useState<Person | null>(null);
  const [scheduleRemarks, setScheduleRemarks] = useState<ScheduleRemark[]>([]);
  const [remarksLoading, setRemarksLoading] = useState(false);
  const [remarkFormVisible, setRemarkFormVisible] = useState(false);
  const [editRemark, setEditRemark] = useState<ScheduleRemark | null>(null);
  const [remarkType, setRemarkType] = useState<ScheduleRemarkType>("note");
  const [remarkSelectedDays, setRemarkSelectedDays] = useState<
    ScheduleRemarkWeekday[]
  >([]);
  const [remarkText, setRemarkText] = useState("");
  const [remarkStartWeek, setRemarkStartWeek] = useState(getCurrentWeek());
  const [remarkStartYear, setRemarkStartYear] = useState(
    new Date().getFullYear()
  );
  const [remarkEndWeek, setRemarkEndWeek] = useState(getCurrentWeek());
  const [remarkEndYear, setRemarkEndYear] = useState(new Date().getFullYear());
  const [remarkOngoing, setRemarkOngoing] = useState(true);
  const [remarkClosePrevious, setRemarkClosePrevious] = useState(false);

  const [rolesModalVisible, setRolesModalVisible] = useState(false);
  const [selectedPersonForRoles, setSelectedPersonForRoles] =
    useState<Person | null>(null);
  const [allRoles, setAllRoles] = useState<AppRole[]>([]);
  const [personRoleAssignments, setPersonRoleAssignments] = useState<
    PersonRoleAssignment[]
  >([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesSaving, setRolesSaving] = useState(false);

  const toast = useToast();
  const { refreshAppRoles } = useApiAuth();

  const load = async () => {
    setLoading(true);
    const res = await apiService.getPeople();
    if (res.success) setPeople(res.data);
    else toast.showError("Failed", res.message || "Unable to load people");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      people.filter((person) => {
        const searchText =
          `${person.name} ${person.surname} ${person.email}`.toLowerCase();
        return searchText.includes(filter.toLowerCase());
      }),
    [people, filter]
  );

  const openNew = () => {
    setEditPerson(null);
    setFormName("");
    setFormSurname("");
    setFormEmail("");
    setFormPlannable(true);
    setModalVisible(true);
  };

  const openEdit = (person: Person) => {
    setEditPerson(person);
    setFormName(person.name);
    setFormSurname(person.surname);
    setFormEmail(person.email);
    setFormPlannable(person.plannable);
    setModalVisible(true);
  };

  const openAvailabilityModal = (person: Person) => {
    setSelectedPersonForAvailability(person);
    setAvailabilityStartWeek(getCurrentWeek());
    setAvailabilityStartYear(new Date().getFullYear());
    setAvailabilityHours(40);
    setAvailabilityModalVisible(true);
  };

  const resetRemarkForm = () => {
    setRemarkType("note");
    setRemarkSelectedDays([]);
    setRemarkText("");
    setRemarkStartWeek(getCurrentWeek());
    setRemarkStartYear(new Date().getFullYear());
    setRemarkEndWeek(getCurrentWeek());
    setRemarkEndYear(new Date().getFullYear());
    setRemarkOngoing(true);
    setRemarkClosePrevious(false);
    setEditRemark(null);
  };

  const loadScheduleRemarks = async (personId: number) => {
    setRemarksLoading(true);
    const res = await apiService.getPersonScheduleRemarks(personId);
    if (res.success) {
      setScheduleRemarks(res.data);
    } else {
      toast.showError("Failed", res.message || "Unable to load schedule remarks");
    }
    setRemarksLoading(false);
  };

  const openRemarksModal = async (person: Person) => {
    setSelectedPersonForRemarks(person);
    setRemarksModalVisible(true);
    resetRemarkForm();
    setRemarkFormVisible(false);
    await loadScheduleRemarks(person.id);
  };

  const openRolesModal = async (person: Person) => {
    setSelectedPersonForRoles(person);
    setRolesModalVisible(true);
    setRolesLoading(true);
    setSelectedRoleIds([]);
    setPersonRoleAssignments([]);

    const [rolesRes, assignedRes] = await Promise.all([
      apiService.getRoles(),
      apiService.getPersonRoles(person.id),
    ]);

    if (rolesRes.success) {
      setAllRoles(rolesRes.data);
    } else {
      toast.showError("Failed", rolesRes.message || "Unable to load roles");
      setAllRoles([]);
    }

    if (assignedRes.success) {
      setPersonRoleAssignments(assignedRes.data);
      setSelectedRoleIds(assignedRes.data.map((role) => role.id));
    } else {
      toast.showError(
        "Failed",
        assignedRes.message || "Unable to load person roles"
      );
      setPersonRoleAssignments([]);
      setSelectedRoleIds([]);
    }

    setRolesLoading(false);
  };

  const savePersonRoles = async () => {
    if (!selectedPersonForRoles) return;

    setRolesSaving(true);
    const currentIds = new Set(personRoleAssignments.map((role) => role.id));
    const nextIds = new Set(selectedRoleIds);
    const toAssign = selectedRoleIds.filter((id) => !currentIds.has(id));
    const toUnassign = personRoleAssignments
      .filter((role) => !nextIds.has(role.id))
      .map((role) => role.id);

    const errors: string[] = [];

    for (const roleId of toAssign) {
      const res = await apiService.assignPersonRole(selectedPersonForRoles.id, {
        role_id: roleId,
      });
      if (!res.success) {
        errors.push(res.message || `Failed to assign role ${roleId}`);
      }
    }

    for (const roleId of toUnassign) {
      const res = await apiService.unassignPersonRole(
        selectedPersonForRoles.id,
        roleId
      );
      if (!res.success) {
        errors.push(res.message || `Failed to unassign role ${roleId}`);
      }
    }

    setRolesSaving(false);

    if (errors.length > 0) {
      toast.showError("Error", errors[0]);
      return;
    }

    toast.showSuccess(
      "Saved",
      `Roles updated for ${selectedPersonForRoles.name} ${selectedPersonForRoles.surname}`
    );
    setRolesModalVisible(false);
    await refreshAppRoles();
  };

  const formatAssignedAt = (value: string) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const openNewRemarkForm = () => {
    resetRemarkForm();
    setRemarkFormVisible(true);
  };

  const openEditRemarkForm = (remark: ScheduleRemark) => {
    setEditRemark(remark);
    setRemarkType(remark.remark_type || "note");
    setRemarkSelectedDays(parseScheduleRemarkDays(remark.days));
    setRemarkText(remark.text);
    const startParts = weekPartsFromId(remark.start_week);
    setRemarkStartWeek(startParts.week);
    setRemarkStartYear(startParts.year);
    if (remark.end_week != null) {
      const endParts = weekPartsFromId(remark.end_week);
      setRemarkEndWeek(endParts.week);
      setRemarkEndYear(endParts.year);
      setRemarkOngoing(false);
    } else {
      setRemarkEndWeek(getCurrentWeek());
      setRemarkEndYear(new Date().getFullYear());
      setRemarkOngoing(true);
    }
    setRemarkClosePrevious(false);
    setRemarkFormVisible(true);
  };

  const toggleRemarkDay = (day: ScheduleRemarkWeekday, checked: boolean) => {
    setRemarkSelectedDays((current) => {
      if (checked) {
        return current.includes(day) ? current : [...current, day];
      }
      return current.filter((selectedDay) => selectedDay !== day);
    });
  };

  const saveRemark = async () => {
    if (!selectedPersonForRemarks) return;
    if (!remarkText.trim()) {
      toast.showWarn("Validation", "Description is required");
      return;
    }

    if (
      (remarkType === "off" || remarkType === "constraint") &&
      remarkSelectedDays.length === 0
    ) {
      toast.showWarn("Validation", "Select at least one weekday");
      return;
    }

    const startWeek = weekIdFromParts(remarkStartYear, remarkStartWeek);
    const daysValue =
      remarkSelectedDays.length > 0
        ? joinScheduleRemarkDays(remarkSelectedDays)
        : null;

    const payload = {
      remark_type: remarkType,
      days: daysValue,
      text: remarkText.trim(),
      start_week: startWeek,
      end_week: remarkOngoing
        ? null
        : weekIdFromParts(remarkEndYear, remarkEndWeek),
      close_previous: !editRemark ? remarkClosePrevious : undefined,
    };

    const res = editRemark
      ? await apiService.updatePersonScheduleRemark(
          selectedPersonForRemarks.id,
          editRemark.id,
          payload
        )
      : await apiService.createPersonScheduleRemark(
          selectedPersonForRemarks.id,
          payload
        );

    if (res.success) {
      toast.showSuccess(
        "Saved",
        editRemark ? "Schedule remark updated" : "Schedule remark created"
      );
      setRemarkFormVisible(false);
      resetRemarkForm();
      await loadScheduleRemarks(selectedPersonForRemarks.id);
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const deleteRemark = async (remark: ScheduleRemark) => {
    if (!selectedPersonForRemarks) return;

    const res = await apiService.deletePersonScheduleRemark(
      selectedPersonForRemarks.id,
      remark.id
    );
    if (res.success) {
      toast.showSuccess("Deleted", "Schedule remark deleted");
      await loadScheduleRemarks(selectedPersonForRemarks.id);
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const save = async () => {
    if (!formName.trim()) {
      toast.showWarn("Validation", "Name is required");
      return;
    }
    if (!formSurname.trim()) {
      toast.showWarn("Validation", "Surname is required");
      return;
    }
    if (!formEmail.trim()) {
      toast.showWarn("Validation", "Email is required");
      return;
    }

    const payload = {
      id: editPerson?.id,
      name: formName.trim(),
      surname: formSurname.trim(),
      email: formEmail.trim(),
      plannable: formPlannable,
    };

    const res = await apiService.createOrUpdatePerson(payload);
    if (res.success) {
      toast.showSuccess(
        "Saved",
        `Person '${res.data.name} ${res.data.surname}' saved`
      );
      setModalVisible(false);
      await load();
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const saveAvailability = async () => {
    if (!selectedPersonForAvailability) return;

    const startWeekFormatted = parseInt(
      `${availabilityStartYear}${availabilityStartWeek
        .toString()
        .padStart(2, "0")}`
    );

    const payload: Availability = {
      startweek: startWeekFormatted,
      hours: availabilityHours,
    };

    const res = await apiService.setAvailability(
      selectedPersonForAvailability.id,
      payload
    );
    if (res.success) {
      toast.showSuccess(
        "Availability Updated",
        `Availability set to ${availabilityHours} hours/week starting from week ${availabilityStartWeek}, ${availabilityStartYear} for ${selectedPersonForAvailability.name} ${selectedPersonForAvailability.surname}`
      );
      setAvailabilityModalVisible(false);
    } else {
      toast.showError("Error", res.message || "Failed to update availability");
    }
  };

  const remove = async (person: Person) => {
    const res = await apiService.deletePerson(person.id);
    if (res.success) {
      toast.showSuccess(
        "Deleted",
        `Person '${person.name} ${person.surname}' deleted`
      );
      await load();
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const plannableBodyTemplate = (rowData: Person) => {
    return rowData.plannable ? (
      <Tag severity="success" value="Plannable" icon="pi pi-check" />
    ) : (
      <Tag severity="secondary" value="Not Plannable" icon="pi pi-times" />
    );
  };

  const nameBodyTemplate = (rowData: Person) => {
    return `${rowData.name} ${rowData.surname}`;
  };

  const actionsBodyTemplate = (rowData: Person) => {
    return (
      <div className="flex gap-2 justify-content-end">
        <Button
          icon="pi pi-shield"
          className="p-button-rounded p-button-outlined p-button-sm"
          onClick={() => openRolesModal(rowData)}
          tooltip="Assign roles"
        />
        <Button
          icon="pi pi-comment"
          className="p-button-rounded p-button-outlined p-button-sm p-button-warning"
          onClick={() => openRemarksModal(rowData)}
          tooltip="Schedule remarks"
        />
        <Button
          icon="pi pi-calendar"
          className="p-button-rounded p-button-outlined p-button-sm p-button-info"
          onClick={() => openAvailabilityModal(rowData)}
          tooltip="Set Availability"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined p-button-sm"
          onClick={() => openEdit(rowData)}
          tooltip="Edit"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
          onClick={() => remove(rowData)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search people..."
          />
        </IconField>
      </div>
      <Button label="Add" icon="pi pi-plus" onClick={openNew} />
    </div>
  );

  return (
    <div>
      <DataTable
        value={filtered}
        loading={loading}
        header={header}
        emptyMessage="No people found"
        className="p-datatable-sm"
        paginator
        rows={10}
      >
        <Column field="name" header="Name" body={nameBodyTemplate} sortable />
        <Column field="email" header="Email" sortable />
        <Column
          field="plannable"
          header="Status"
          body={plannableBodyTemplate}
          sortable
          style={{ width: "150px" }}
        />
        <Column
          header="Actions"
          body={actionsBodyTemplate}
          style={{ width: "260px" }}
          headerStyle={{ textAlign: "right" }}
        />
      </DataTable>

      {/* Assign Roles Modal */}
      <Dialog
        header={
          selectedPersonForRoles
            ? `Roles: ${selectedPersonForRoles.name} ${selectedPersonForRoles.surname}`
            : "Assign roles"
        }
        visible={rolesModalVisible}
        onHide={() => setRolesModalVisible(false)}
        style={{ width: 560 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label className="block mb-2">App roles</label>
            <MultiSelect
              value={selectedRoleIds}
              options={allRoles}
              onChange={(e) => setSelectedRoleIds(e.value || [])}
              optionLabel="name"
              optionValue="id"
              placeholder={rolesLoading ? "Loading..." : "Select roles"}
              className="w-full"
              display="chip"
              disabled={rolesLoading || rolesSaving}
              filter
            />
          </div>

          {personRoleAssignments.length > 0 && (
            <div className="field">
              <label className="block mb-2">Current assignments</label>
              <DataTable
                value={personRoleAssignments}
                className="p-datatable-sm"
                emptyMessage="No roles assigned"
              >
                <Column field="name" header="Role" />
                <Column field="role_key" header="Key" />
                <Column
                  header="Assigned at"
                  body={(row: PersonRoleAssignment) =>
                    formatAssignedAt(row.assigned_at)
                  }
                />
                <Column field="assigned_by" header="Assigned by" />
              </DataTable>
            </div>
          )}

          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setRolesModalVisible(false)}
              disabled={rolesSaving}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={savePersonRoles}
              loading={rolesSaving}
              disabled={rolesLoading}
            />
          </div>
        </div>
      </Dialog>

      {/* Person Edit/Create Modal */}
      <Dialog
        header={editPerson ? "Edit Person" : "Add Person"}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: 500 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="grid">
            <div className="col-6">
              <div className="field">
                <label className="block mb-2">Name *</label>
                <InputText
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full"
                  placeholder="Enter first name"
                />
              </div>
            </div>
            <div className="col-6">
              <div className="field">
                <label className="block mb-2">Surname *</label>
                <InputText
                  value={formSurname}
                  onChange={(e) => setFormSurname(e.target.value)}
                  className="w-full"
                  placeholder="Enter surname"
                />
              </div>
            </div>
          </div>

          <div className="field">
            <label className="block mb-2">Email *</label>
            <InputText
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              className="w-full"
              placeholder="Enter email address"
              type="email"
            />
          </div>

          <div className="field">
            <div className="flex align-items-center">
              <Checkbox
                id="plannable"
                checked={formPlannable}
                onChange={(e) => setFormPlannable(e.checked || false)}
                className="mr-2"
              />
              <label htmlFor="plannable" className="block">
                Plannable
              </label>
            </div>
            <small className="text-500">
              Mark if this person can be included in project planning
            </small>
          </div>

          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setModalVisible(false)}
            />
            <Button label="Save" icon="pi pi-check" onClick={save} />
          </div>
        </div>
      </Dialog>

      {/* Availability Modal */}
      <Dialog
        header={`Set Availability - ${selectedPersonForAvailability?.name} ${selectedPersonForAvailability?.surname}`}
        visible={availabilityModalVisible}
        onHide={() => setAvailabilityModalVisible(false)}
        style={{ width: 500 }}
        modal
      >
        <div className="flex flex-column gap-4">
          <div className="field">
            <label className="block mb-2">Start Week</label>
            <WeekPicker
              value={availabilityStartWeek}
              year={availabilityStartYear}
              onChange={(weekNumber, year) => {
                setAvailabilityStartWeek(weekNumber);
                setAvailabilityStartYear(year);
              }}
            />
            <small className="text-500">
              Select the week from which this availability takes effect
            </small>
          </div>

          <div className="field">
            <label className="block mb-2">
              Hours per Week: {availabilityHours}
            </label>
            <Slider
              value={availabilityHours}
              onChange={(e) =>
                setAvailabilityHours(
                  Array.isArray(e.value) ? e.value[0] : e.value
                )
              }
              min={0}
              max={40}
              step={1}
              className="w-full"
            />
            <div className="flex justify-content-between mt-2 text-sm text-500">
              <span>0 hours</span>
              <span>40 hours</span>
            </div>
            <small className="text-500">
              Number of hours per week this person is available for planning
            </small>
          </div>

          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setAvailabilityModalVisible(false)}
            />
            <Button
              label="Set Availability"
              icon="pi pi-calendar-plus"
              onClick={saveAvailability}
              className="p-button-success"
            />
          </div>
        </div>
      </Dialog>

      {/* Schedule remarks modal */}
      <Dialog
        header={`Schedule remarks - ${selectedPersonForRemarks?.name} ${selectedPersonForRemarks?.surname}`}
        visible={remarksModalVisible}
        onHide={() => setRemarksModalVisible(false)}
        style={{ width: 720 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="flex justify-content-between align-items-center">
            <span className="text-500">
              Recurring schedule notes shown on the schedule employee view.
            </span>
            <Button
              label="Add remark"
              icon="pi pi-plus"
              size="small"
              onClick={openNewRemarkForm}
              disabled={remarkFormVisible}
            />
          </div>

          {remarkFormVisible && (
            <div className="surface-100 border-round p-3 flex flex-column gap-3">
              <div className="text-sm font-semibold">
                {editRemark ? "Edit remark" : "New remark"}
              </div>
              <div className="field">
                <label className="block mb-2">Type</label>
                <SelectButton
                  value={remarkType}
                  options={remarkTypeOptions}
                  onChange={(e) => {
                    if (e.value) {
                      setRemarkType(e.value);
                    }
                  }}
                  className="w-full"
                />
              </div>
              <div className="field">
                <label className="block mb-2">Weekdays</label>
                <div className="flex flex-wrap gap-3">
                  {SCHEDULE_REMARK_WEEKDAYS.map((day) => (
                    <div key={day} className="flex align-items-center">
                      <Checkbox
                        inputId={`remark-day-${day}`}
                        checked={remarkSelectedDays.includes(day)}
                        onChange={(e) =>
                          toggleRemarkDay(day, e.checked ?? false)
                        }
                        className="mr-2"
                      />
                      <label htmlFor={`remark-day-${day}`}>{day}</label>
                    </div>
                  ))}
                </div>
                <small className="text-500">
                  Required for off and constraint remarks. Use description for
                  details like afternoons.
                </small>
              </div>
              <div className="field">
                <label className="block mb-2">Description *</label>
                <InputTextarea
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  className="w-full"
                  rows={3}
                  placeholder="e.g. afternoons off, don't schedule meetings"
                />
              </div>
              <div className="field">
                <label className="block mb-2">Start week</label>
                <WeekPicker
                  value={remarkStartWeek}
                  year={remarkStartYear}
                  onChange={(weekNumber, year) => {
                    setRemarkStartWeek(weekNumber);
                    setRemarkStartYear(year);
                  }}
                />
              </div>
              <div className="field">
                <div className="flex align-items-center mb-2">
                  <Checkbox
                    id="remark-ongoing"
                    checked={remarkOngoing}
                    onChange={(e) => setRemarkOngoing(e.checked || false)}
                    className="mr-2"
                  />
                  <label htmlFor="remark-ongoing">Ongoing (no end week)</label>
                </div>
                {!remarkOngoing && (
                  <WeekPicker
                    value={remarkEndWeek}
                    year={remarkEndYear}
                    minWeek={remarkStartWeek}
                    minYear={remarkStartYear}
                    onChange={(weekNumber, year) => {
                      setRemarkEndWeek(weekNumber);
                      setRemarkEndYear(year);
                    }}
                  />
                )}
              </div>
              {!editRemark && (
                <div className="field">
                  <div className="flex align-items-center">
                    <Checkbox
                      id="remark-close-previous"
                      checked={remarkClosePrevious}
                      onChange={(e) =>
                        setRemarkClosePrevious(e.checked || false)
                      }
                      className="mr-2"
                    />
                    <label htmlFor="remark-close-previous">
                      Close previous open-ended remark
                    </label>
                  </div>
                </div>
              )}
              <div className="flex justify-content-end gap-2">
                <Button
                  label="Cancel"
                  className="p-button-text"
                  onClick={() => {
                    setRemarkFormVisible(false);
                    resetRemarkForm();
                  }}
                />
                <Button
                  label="Save remark"
                  icon="pi pi-check"
                  onClick={saveRemark}
                />
              </div>
            </div>
          )}

          <DataTable
            value={scheduleRemarks}
            loading={remarksLoading}
            emptyMessage="No schedule remarks"
            className="p-datatable-sm"
            size="small"
          >
            <Column
              header="Type"
              body={(row: ScheduleRemark) =>
                formatScheduleRemarkType(row.remark_type)
              }
            />
            <Column
              header="Days"
              body={(row: ScheduleRemark) =>
                formatScheduleRemarkDays(row.days) || "—"
              }
            />
            <Column
              field="text"
              header="Description"
              body={(row: ScheduleRemark) =>
                row.text.length > 60
                  ? `${row.text.slice(0, 60)}…`
                  : row.text
              }
            />
            <Column
              header="Valid"
              body={(row: ScheduleRemark) =>
                formatScheduleRemarkRange(row.start_week, row.end_week)
              }
            />
            <Column
              header=""
              style={{ width: "100px" }}
              body={(row: ScheduleRemark) => (
                <div className="flex gap-1 justify-content-end">
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-outlined p-button-sm"
                    onClick={() => openEditRemarkForm(row)}
                    tooltip="Edit"
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                    onClick={() => deleteRemark(row)}
                    tooltip="Delete"
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </Dialog>
    </div>
  );
};

export default PeopleAdmin;
